import React, { useCallback, useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardText,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { CardHeaderWithToolbars, Title } from '../../components/Card/CardHeaderWithToolbars';
import FilterContainer from '../../components/Container/FilterContainer/FilterContainer';
import { ModalInfoCreditCard } from '../../components/Modals';
import SalesCursorPagination from '../../components/Paginations/SalesCursorPagination';
import { Tbl, TblBody, TblHead } from '../../components/Tbl';
import privateView from '../../components/hocs/privateView';
import { DEFAULT_PER_PAGE, DEFAULT_SORT_BY } from '../../constants/settings';
import { getSaleList, submitSaleReturn } from '../../actions/saleAction';
import { searchUsersByKeyword } from '../../actions/userAction';
import Analytics from '../../services/Analytics';
import FiltersPanel from '../Sales/Filters';
import RowHeader from '../Sales/Monitoring/rowItem/RowHeader';
import OutstandingRowHeader from '../Sales/Outstanding/RowHeader';
import FollowUpRowHeader from '../Sales/FollowUp/TblRow/RowHeader';
import DeliveryRowHeader from '../Sales/Delivering/RowHeader';
import NeedRefundRowHeader from '../Sales/Failed/failedSaleList/RowHeader';
import ReturnActionModal from './ReturnActionModal';
import ReturnRow from './ReturnRow';
import Toolbars from './Toolbars';
import { listErrorMessage, returnActionPath } from './returnSale';

const cleanSaleListParams = (rawParams) => {
  let next = { ...rawParams };
  if (next.voucher) {
    const { voucher, ...other } = next;
    next = Number(voucher.id)
      ? { ...other, voucher_id: voucher.id }
      : { ...other, voucher_code: voucher.code };
  }
  if (next.seller && next.seller.id) {
    const { seller, ...other } = next;
    next = { ...other, seller_id: seller.id };
  }
  return Object.keys(next)
    .filter((key) => next[key] !== '' && next[key] != null)
    .reduce((obj, key) => ({ ...obj, [key]: next[key] }), {});
};

const ReturnsContainer = ({
  title,
  description,
  type,
  createdSource,
  courier,
  queue,
  analyticsPageName,
  ...props
}) => {
  const lockedParams = () => ({
    type,
    sort_by: DEFAULT_SORT_BY,
    per_page: DEFAULT_PER_PAGE,
    ...(createdSource ? { created_source: createdSource } : {}),
    ...(courier ? { courier } : {}),
    ...(queue === 'relisting' ? { return_relist_state: 'all' } : {}),
  });

  const [params, setParams] = useState(lockedParams);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isResetFilter, setIsResetFilter] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [actionSale, setActionSale] = useState(null);
  const [actionKind, setActionKind] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [listAccepted, setListAccepted] = useState(false);
  const [draftRelistingFilter, setDraftRelistingFilter] = useState('all');
  const mountedRef = useRef(true);
  const [modalCreditCard, setModalCreditCard] = useState(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const { getList } = props;
  const pageViewRef = useRef({});
  pageViewRef.current = {
    analyticsPageName,
    email: props.injectedProps && props.injectedProps.email,
    location: props.location,
    match: props.match,
  };

  const loadList = useCallback((rawParams) => {
    const nextParams = cleanSaleListParams(rawParams);
    const appending = nextParams.cursor != null && nextParams.cursor !== '';
    if (!appending && mountedRef.current) setListAccepted(false);
    return getList({ params: nextParams }).then((result) => {
      if (mountedRef.current && result && result.ok) setListAccepted(true);
      return result;
    });
  }, [getList]);

  const refreshList = useCallback(() => {
    const nextParams = { ...paramsRef.current };
    delete nextParams.cursor;
    setParams(nextParams);
    loadList(nextParams);
  }, [loadList]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const view = pageViewRef.current;
    loadList(paramsRef.current).then(() => {
      if (!cancelled) setIsDataLoaded(true);
    });
    Analytics.recordPageView(view.analyticsPageName, view.email, {
      url: window.location.href,
      location: view.location,
      match: view.match,
    });
    return () => {
      cancelled = true;
    };
  }, [loadList]);

  const applyFilter = (payloadFilter) => {
    const nextRelistState = queue === 'relisting' ? draftRelistingFilter : null;
    const nextParams = {
      ...paramsRef.current,
      ...payloadFilter,
      type,
      ...(createdSource ? { created_source: createdSource } : {}),
      ...(courier ? { courier } : {}),
      ...(nextRelistState ? { return_relist_state: nextRelistState } : {}),
    };
    delete nextParams.cursor;
    setParams(nextParams);
    loadList(nextParams);
  };

  const resetFilter = () => {
    setDraftRelistingFilter('all');
    const nextParams = lockedParams();
    setParams(nextParams);
    setIsResetFilter(true);
    loadList(nextParams);
  };

  const handleCategoryChanged = (category) => {
    const nextParams = {
      ...paramsRef.current,
      category: category === '' ? category : category.value,
    };
    delete nextParams.cursor;
    setParams(nextParams);
    loadList(nextParams);
  };

  const handleMarkedClick = (value) => {
    const nextParams = {
      ...paramsRef.current,
      marked: value ? 1 : '',
    };
    delete nextParams.cursor;
    setParams(nextParams);
    loadList(nextParams);
  };

  const handleLoadMore = (cursor) => {
    if (!cursor || props.sale.isFetch) return;
    loadList({ ...paramsRef.current, cursor });
  };

  const closeAction = () => {
    if (props.sale.isSubmit) return;
    setActionSale(null);
    setActionKind(null);
  };

  const submitAction = (id, kind, body) => (
    props.submitReturn(id, returnActionPath(kind), body).then((result) => {
      if (result && result.ok) {
        setActionSale(null);
        setActionKind(null);
        refreshList();
      }
      return result;
    })
  );

  const rows = Array.isArray(props.sale.list.data) ? props.sale.list.data : [];
  const listFailed = listErrorMessage(props.sale.listError);
  const hasLoadedRows = listAccepted && props.sale.list.status_code === 200 && rows.length > 0;
  const visibleRows = rows;
  const hasRows = hasLoadedRows && visibleRows.length > 0;
  const usesSalesLayout = queue === 'outstanding' || queue === 'qc' || queue === 'relisting' || queue === 'refund' || queue === 'follow_up';
  const isDeliveryQueue = queue === 'pre_delivering' || queue === 'delivering';
  const HeaderRow = isDeliveryQueue ? DeliveryRowHeader : queue === 'follow_up' ? FollowUpRowHeader : queue === 'refund' ? NeedRefundRowHeader : usesSalesLayout ? OutstandingRowHeader : RowHeader;
  const columnCount = queue === 'refund' ? 9 : usesSalesLayout ? 8 : 9;

  return (
    <Row>
      <ReturnActionModal
        isOpen={actionSale != null}
        kind={actionKind}
        sale={actionSale}
        isSubmit={props.sale.isSubmit}
        onCancel={closeAction}
        onSubmit={submitAction}
        onSearchUsers={props.searchUsers}
        receivedBy={props.injectedProps && props.injectedProps.email}
      />
      <ModalInfoCreditCard
        isOpen={modalCreditCard}
        toggle={() => setModalCreditCard((open) => !open)}
        invoiceNumber={invoiceNumber}
      />
      <Col xs={12}>
        <Card>
          <CardHeaderWithToolbars>
            <Title>
              <i className="fa fa-align-justify"></i>
              <span>{title}</span>
            </Title>
            {isDataLoaded ? (
              <Toolbars
                isOpen={filterOpen}
                onToggleFilter={() => setFilterOpen((open) => !open)}
                onRefreshList={refreshList}
                onResetFilter={resetFilter}
              />
            ) : null}
          </CardHeaderWithToolbars>
          <FilterContainer>
            <FiltersPanel
              {...params}
              hideCreatedSourceFilter={Boolean(createdSource)}
              onApplyFilter={applyFilter}
              isReset={isResetFilter}
              onResetFilterCallback={() => { setIsResetFilter(false); setFilterOpen(false); }}
              isOpen={filterOpen}
              additionalFilters={queue === 'relisting' ? (
                <Col xs={12} sm={3}>
                  <FormGroup row className="mb-3">
                    <Col xs={12} sm={4} md={3}><Label for="return-relisting-filter" className="justify-content-start">Relisting</Label></Col>
                    <Col xs={12} sm={8} md={9}>
                      <Input id="return-relisting-filter" type="select" className="w-100" value={draftRelistingFilter} onChange={(event) => setDraftRelistingFilter(event.target.value)}>
                        <option value="all">All</option>
                        <option value="pending">Not Relisted</option>
                        <option value="completed">Relisted</option>
                      </Input>
                    </Col>
                  </FormGroup>
                </Col>
              ) : null}
            />
          </FilterContainer>
          <CardBody>
            <CardText>{description}</CardText>
            <div className="table-responsive">
              <Tbl>
                <TblHead>
                  <HeaderRow
                    showSellId={usesSalesLayout || isDeliveryQueue}
                    showReturnStatus={isDeliveryQueue}
                    marked={params.marked}
                    onMarkedClick={handleMarkedClick}
                    categoryName={params.category || null}
                    categoryValue={params.category}
                    onCategoryChanged={handleCategoryChanged}
                  />
                </TblHead>
                <TblBody
                  isLoad={props.sale.isFetch && !props.sale.isLoadingMore}
                  hasRow={hasRows || Boolean(listFailed)}
                  columnCount={columnCount}
                  emptyRowText="No returns found."
                >
                  {hasRows ? visibleRows.map((sale, index) => (
                    <ReturnRow
                      key={sale.id}
                      rowId={index + 1}
                      sale={sale}
                      queue={queue}
                      loginId={props.injectedProps && props.injectedProps.id}
                      onOpenAction={(item, kind) => {
                        setActionSale(item);
                        setActionKind(kind);
                      }}
                      onOpenCreditCard={() => {
                        setInvoiceNumber(sale.invoice_number);
                        setModalCreditCard(true);
                      }}
                    />
                  )) : listFailed ? (
                    <tr>
                      <td colSpan={columnCount}>
                        <Alert color="danger" className="mb-2">{listFailed}</Alert>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={refreshList}
                          disabled={props.sale.isFetch}
                        >
                          Retry
                        </Button>
                      </td>
                    </tr>
                  ) : null}
                </TblBody>
              </Tbl>
            </div>
            <SalesCursorPagination
              loadedCount={hasLoadedRows ? rows.length : 0}
              hasMore={Boolean(hasLoadedRows && props.sale.list.has_more)}
              nextCursor={hasLoadedRows ? props.sale.list.next_cursor : null}
              isLoading={props.sale.isLoadingMore}
              isFetching={props.sale.isFetch}
              error={hasLoadedRows ? props.sale.listError : null}
              onLoadMore={handleLoadMore}
              onRetry={refreshList}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

ReturnsContainer.propTypes = {
  title: propTypes.string.isRequired,
  description: propTypes.string.isRequired,
  type: propTypes.string.isRequired,
  createdSource: propTypes.string,
  courier: propTypes.string,
  queue: propTypes.string,
  analyticsPageName: propTypes.string.isRequired,
  getList: propTypes.func,
  submitReturn: propTypes.func,
  searchUsers: propTypes.func,
  sale: propTypes.object,
  location: propTypes.object,
  match: propTypes.object,
  injectedProps: propTypes.object,
};

ReturnsContainer.defaultProps = {
  createdSource: null,
  courier: null,
  queue: null,
  sale: { list: {}, isFetch: false, isSubmit: false, listError: null },
};

const mapStateToProps = ({ sale, auth: { isFetch, isLogged, roles, id, email } }) => ({
  sale,
  guardData: { isFetch, isLogged, roles, id, email },
});

const mapDispatchToProps = {
  getList: getSaleList,
  submitReturn: submitSaleReturn,
  searchUsers: searchUsersByKeyword,
};

export const guardReturns = (View, roles) => connect(
  mapStateToProps,
  mapDispatchToProps
)(privateView(View, roles));

export { ReturnsContainer };

export default guardReturns(ReturnsContainer, ['supervisor', 'sale_supervisor']);
